// Song usage log for CCLI reporting: one entry each time a song is sent live,
// and helpers to total it up for a date range and export it.

export const DEDUPE_MS = 10 * 60 * 1000; // sending the same song live again within 10 min is one use
export const MAX_ENTRIES = 5000;         // keeps the synced value well under the 1 MB limit

const norm = (s) => String(s || '').toLowerCase().trim();

export const newUsageId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** A log entry: { id, title, ccli, at (ISO time) }. */
export function makeEntry({ title, ccli = '' }, now = Date.now(), id = newUsageId()) {
  return { id, title: String(title || '').trim(), ccli: String(ccli || '').trim(), at: new Date(now).toISOString() };
}

/** The log with `entry` added, or unchanged if it repeats the same song within DEDUPE_MS. */
export function recordUse(log, entry) {
  const list = Array.isArray(log) ? log : [];
  if (!entry.title) return list;
  const at = Date.parse(entry.at);
  const repeat = list.some(e => norm(e.title) === norm(entry.title) && Math.abs(at - Date.parse(e.at)) < DEDUPE_MS);
  if (repeat) return list;
  return [...list, entry].slice(-MAX_ENTRIES);
}

export const RANGES = [
  { id: 'month', label: 'This month' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
];

/** { from: Date | null, to: Date } for a preset, relative to `now`. */
export function rangeFor(preset, now = new Date()) {
  const to = new Date(now);
  if (preset === 'month') return { from: new Date(now.getFullYear(), now.getMonth(), 1), to };
  if (preset === 'year') return { from: new Date(now.getFullYear(), 0, 1), to };
  if (preset === 'last30') return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), to };
  return { from: null, to };
}

/** Per-song totals within the range: [{ title, ccli, count, lastUsed }], most used first. */
export function summarize(log, { from, to }) {
  const byTitle = new Map();
  (Array.isArray(log) ? log : []).forEach((e) => {
    const t = Date.parse(e.at);
    if (Number.isNaN(t) || (from && t < from.getTime()) || (to && t > to.getTime())) return;
    const key = norm(e.title);
    const row = byTitle.get(key) || { title: e.title, ccli: '', count: 0, lastUsed: e.at };
    row.count += 1;
    if (!row.ccli && e.ccli) row.ccli = e.ccli;
    if (Date.parse(e.at) > Date.parse(row.lastUsed)) row.lastUsed = e.at;
    byTitle.set(key, row);
  });
  return [...byTitle.values()].sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}

// A cell starting with = + - @ would run as a formula when opened in a spreadsheet.
const csvCell = (value) => {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function toCsv(rows) {
  const lines = [['Title', 'CCLI Song Number', 'Times Used', 'Last Used']];
  rows.forEach(r => lines.push([r.title, r.ccli, r.count, r.lastUsed.slice(0, 10)]));
  return lines.map(line => line.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
